import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import swal from "sweetalert";
import PageTitle from "../../../jsx/layouts/PageTitle";
import { formatDateTime } from "../../../utils/dateTimeFormate";
import { getCmTrophyApplicant, updateCmTrophyApplicationStatus } from "../../../services/adminApiService/cmTrophyApi/cmTrophyApi";
import { AuthContext } from "../../../context/AuthContext";
import ConfirmModal from "../editProfile/ConfirmModal";
import { canDecideCmTrophy } from "./cmTrophyPermissions";

const Field = ({ label, value }) => (
  <div className="col-md-4 col-sm-6 mb-3">
    <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
    <div style={{ fontWeight: 500 }}>{value || value === 0 ? value : '—'}</div>
  </div>
);

const CmTrophyApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);

  const fetchApplication = () => {
    setLoading(true);
    return getCmTrophyApplicant(id)
      .then((res) => setApplication(res?.data?.data || null))
      .catch((err) => console.error('CM Trophy application detail error:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchApplication();
  }, [id]);

  const canDecide = canDecideCmTrophy(Number(userData?.user_level), userData?.email);

  const handleDecide = async (status, remark = '') => {
    setDeciding(true);
    try {
      await updateCmTrophyApplicationStatus(id, status, remark);
      await fetchApplication();
      swal('Success', `Application ${status}.`, 'success');
    } catch (error) {
      swal('Error', error?.response?.data?.message || 'Failed to update application status.', 'error');
    } finally {
      setDeciding(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Application Detail" motherMenu="CM Trophy" />
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : !application ? (
            <div className="text-center">Application not found.</div>
          ) : (
            <>
              <div className="row">
                <Field label="Name" value={application?.name} />
                <Field label="Application Code" value={application?.application_code} />
                <Field label="Email" value={application?.email} />
                <Field label="Phone" value={application?.phone} />
                <Field label="DOB" value={application?.dob ? formatDateTime(application.dob) : ''} />
                <Field label="Gender" value={application?.gender} />
                <Field label="Age Category" value={application?.age_category} />
                <Field label="Aadhar Number" value={application?.aadhar_number} />
                <Field label="Father Name" value={application?.father_name} />
                <Field label="Mother Name" value={application?.mother_name} />
                <Field label="Bank Name" value={application?.bank_name} />
                <Field label="Account Holder Name" value={application?.account_holder_name} />
                <Field label="Bank Account Number" value={application?.bank_account_number} />
                <Field label="IFSC Code" value={application?.ifsc_code} />
                <Field label="Achievements" value={application?.achievements} />
                <Field label="Education Qualification" value={application?.education_qualification} />
                <Field label="Education Institution" value={application?.education_institution} />
                <Field label="Category" value={application?.category} />
                <Field label="Has Disability" value={application?.has_disability ? 'Yes' : 'No'} />
                <Field label="Registration Level" value={application?.registration_level} />
                <Field label="Sansad" value={application?.sansad?.name} />
                <Field label="Vidhan Sabha" value={application?.vidhan_sabha?.name} />
                <Field label="Nyay Panchayat" value={application?.nyay_panchayat?.name} />
                <Field label="Sport" value={application?.sport} />
                <Field label="Events" value={Array.isArray(application?.events) ? application.events.join(', ') : application?.events} />
                <Field label="Status" value={application?.status} />
                {application?.status === 'rejected' && (
                  <Field label="Rejection Reason" value={application?.admin_remark} />
                )}
                <Field label="Applied On" value={application?.created_at ? formatDateTime(application.created_at) : ''} />
              </div>

              {Array.isArray(application?.docs) && application.docs.length > 0 && (
                <div className="mt-3">
                  <div className="text-muted mb-2" style={{ fontSize: 12 }}>Documents</div>
                  <div className="d-flex flex-wrap" style={{ gap: '1.5rem' }}>
                    {application.docs.map((doc) => {
                      const label = doc?.doc_type === 'birth_education_certificate'
                        ? 'Birth / Education Certificate'
                        : (doc?.doc_type || `document_${doc?.id}`).replace(/_/g, ' ');
                      const isPhoto = doc?.doc_type === 'photo';
                      return (
                        <a
                          key={doc?.id}
                          href={doc?.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="mb-3 text-center"
                          style={{ textTransform: 'capitalize' }}
                        >
                          {isPhoto ? (
                            <img
                              src={doc?.file_url}
                              alt={label}
                              style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, display: 'block', marginBottom: 4 }}
                            />
                          ) : (
                            <div
                              style={{ width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f1f5', borderRadius: 8, marginBottom: 4 }}
                            >
                              <i className="bi bi-file-earmark-text" style={{ fontSize: 28, color: '#8757e8' }} />
                            </div>
                          )}
                          <div style={{ fontSize: 12, width: 100 }}>{label}</div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className="mt-3 d-flex gap-2">
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>Back</Button>
        {canDecide && (
          <>
            <Button variant="success" disabled={deciding} onClick={() => handleDecide('approved')}>Approve</Button>
            <Button variant="danger" disabled={deciding} onClick={() => setShowRejectConfirm(true)}>Reject</Button>
          </>
        )}
      </div>
      <ConfirmModal
        type="reject"
        show={showRejectConfirm}
        handleClose={() => setShowRejectConfirm(false)}
        onConfirm={(remark) => handleDecide('rejected', remark)}
        reasonOptions={[
          "Male above 19 are not eligible to participate in CM Championship Trophy",
          "Invalid aadhar details provided",
          "False Documents",
        ]}
      />
    </>
  );
};

export default CmTrophyApplicationDetail;
